import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse, Button, Pagination, Alert } from 'react-bootstrap';
import Swal from '../helpers/swal';
import surveyService from '../../services/survey.service';
import Loading from '../common/Loading';
import ProcessQueueFilters from '../process/ProcessQueueFilters';
import { formatStatusLabel, truncateText, toProperCase } from '../process/processQueueUtils';

const SORT_FIELDS = [
    { value: 'createdAt', label: 'Created At' },
    { value: 'modifiedAt', label: 'Modified At' },
    { value: 'title', label: 'Title' },
    { value: 'category', label: 'Category' },
    { value: 'status', label: 'Status' },
    { value: 'startDate', label: 'Start Date' },
    { value: 'endDate', label: 'End Date' },
];

const STATUS_OPTIONS = ['DRAFT', 'ACTIVE', 'CLOSED', 'ARCHIVED'];

const SURVEY_STATUS_BADGES = {
    DRAFT: 'text-bg-secondary',
    ACTIVE: 'text-bg-success',
    CLOSED: 'text-bg-warning',
    ARCHIVED: 'text-bg-dark',
};

function SurveyTable({ searchPanelOpen = false, onSearchPanelToggle }) {
    const navigate = useNavigate();
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentItems, setCurrentItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const queryOptions = {
        sortBy,
        sortOrder,
        searchField: 'title',
        searchTerm: appliedSearch,
        statusFilter,
    };

    const fetchList = useCallback(() => {
        setIsLoading(true);
        setError(null);
        surveyService
            .getSurveyTemplates(currentPage, itemsPerPage, queryOptions)
            .then((res) => {
                setCurrentItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                setSelectedItems([]);
                if ((res.data.content || []).length === 0 && (appliedSearch || statusFilter)) {
                    setError('No survey templates match your filters.');
                }
            })
            .catch(() => {
                setError('Failed to load survey templates. Please try again.');
                setCurrentItems([]);
            })
            .finally(() => setIsLoading(false));
    }, [currentPage, itemsPerPage, sortBy, sortOrder, appliedSearch, statusFilter]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm === appliedSearch) return;
            setCurrentPage(0);
            setAppliedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, appliedSearch]);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm === appliedSearch) return;
        setCurrentPage(0);
        setAppliedSearch(searchTerm);
    };

    const handleClearTitleSearch = () => {
        setSearchTerm('');
        if (appliedSearch) {
            setCurrentPage(0);
            setAppliedSearch('');
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setStatusFilter('');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleEdit = (id) => {
        navigate(`edit/${id}`);
    };

    const handleCheckboxChange = (event, item) => {
        if (event.target.checked) {
            setSelectedItems((prev) => [...prev, item]);
        } else {
            setSelectedItems((prev) => prev.filter((s) => s.id !== item.id));
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedItems(currentItems);
        } else {
            setSelectedItems([]);
        }
    };

    const confirmAndDelete = async (items) => {
        if (items.length === 0) return;

        const count = items.length;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You are about to delete ${count} selected template${count > 1 ? 's' : ''}. This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger btn-sm',
                cancelButton: 'btn btn-secondary btn-sm',
                actions: 'gap-3',
            },
            buttonsStyling: false,
        });

        if (!result.isConfirmed) return;

        try {
            await Promise.all(items.map((item) => surveyService.deleteSurveyTemplateById(item.id)));
            await Swal.fire({
                title: 'Deleted!',
                text: `${count} template${count > 1 ? 's have' : ' has'} been deleted successfully.`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
                customClass: { title: 'card-title' },
                buttonsStyling: false,
            });
            setSelectedItems([]);
            if (currentPage === 0) {
                fetchList();
            } else {
                setCurrentPage(0);
            }
        } catch {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to delete selected templates. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
                customClass: {
                    title: 'card-title',
                    confirmButton: 'btn btn-secondary btn-sm',
                },
                buttonsStyling: false,
            });
        }
    };

    const handleDeleteSelected = () => confirmAndDelete(selectedItems);

    const handleDeleteOne = (item) => confirmAndDelete([item]);

    const StatusBadge = ({ status }) => {
        const badgeClass = SURVEY_STATUS_BADGES[status] || 'text-bg-secondary';
        return <span className={`badge ${badgeClass}`}>{formatStatusLabel(status)}</span>;
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = currentItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    const formatDateRange = (startDate, endDate) => {
        if (!startDate && !endDate) return '—';
        if (startDate && endDate) return `${startDate} – ${endDate}`;
        return startDate || endDate;
    };

    return (
        <>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                <div className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-ui-checks text-primary"></i>
          <h5 className="card-title mb-0" style={{ color: '#012970' }}>
                        Survey Templates
                        <span className="text-muted fw-normal" style={{ fontSize: '0.9rem', marginLeft: '0.35rem' }}>
                            ({totalElements} template{totalElements !== 1 ? 's' : ''} total)
                        </span>
                    </h5>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto">
                    <form onSubmit={handleSearchSubmit} className="survey-list-title-search">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text" aria-hidden="true">
                                <i className="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by title…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Search by survey title"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleClearTitleSearch}
                                    aria-label="Clear survey title search"
                                    title="Clear search"
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>
                    </form>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm flex-shrink-0"
                        onClick={onSearchPanelToggle}
                        aria-expanded={searchPanelOpen}
                        title={searchPanelOpen ? 'Hide filters' : 'Show filters'}
                    >
                        <i className="bi bi-funnel me-1"></i>
                        Filter
                    </button>
                </div>
            </div>

            <Collapse in={searchPanelOpen}>
                <div>
                    <ProcessQueueFilters
                        showSearch={false}
                        statusFilter={statusFilter}
                        onStatusFilterChange={(value) => {
                            setStatusFilter(value);
                            setCurrentPage(0);
                        }}
                        statusOptions={STATUS_OPTIONS}
                        sortBy={sortBy}
                        onSortByChange={(value) => {
                            setSortBy(value);
                            setCurrentPage(0);
                        }}
                        sortFields={SORT_FIELDS}
                        sortOrder={sortOrder}
                        onSortOrderChange={(value) => {
                            setSortOrder(value);
                            setCurrentPage(0);
                        }}
                        onClear={handleClearFilters}
                        canClear={Boolean(appliedSearch || statusFilter || searchTerm)}
                    />
                </div>
            </Collapse>

            {selectedItems.length > 0 && (
                <div className="mb-3">
                    <button type="button" className="btn btn-danger btn-sm" onClick={handleDeleteSelected}>
                        <i className="bi bi-trash me-1"></i>
                        Delete selected ({selectedItems.length})
                    </button>
                </div>
            )}

            {isLoading ? (
                <Loading />
            ) : error && currentItems.length === 0 ? (
                <Alert variant="warning">{error}</Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '40px' }}>
                                    <input
                                        type="checkbox"
                                        checked={
                                            currentItems.length > 0 &&
                                            selectedItems.length === currentItems.length
                                        }
                                        onChange={handleSelectAll}
                                        aria-label="Select all rows"
                                    />
                                </th>
                                <th>Title / Category</th>
                                <th>Status</th>
                                <th>Date Range</th>
                                <th>Responses</th>
                                <th>Created</th>
                                <th style={{ width: '120px' }}>Actions</th>
                                <th style={{ width: '56px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted py-4">
                                        No survey templates found.
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <tr className={expandedRowId === item.id ? 'table-active' : ''}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.some((s) => s.id === item.id)}
                                                    onChange={(e) => handleCheckboxChange(e, item)}
                                                />
                                            </td>
                                            <td>
                                                <div className="fw-medium">{item.title || '—'}</div>
                                                <small className="text-muted">{toProperCase(item.category)}</small>
                                            </td>
                                            <td>
                                                <StatusBadge status={item.status} />
                                            </td>
                                            <td>
                                                <small>{formatDateRange(item.startDate, item.endDate)}</small>
                                            </td>
                                            <td>
                                                <small>
                                                    {item.targetResponse != null ? `${item.targetResponse} target` : '—'}
                                                    {item.maxResponse != null ? ` · ${item.maxResponse} max` : ''}
                                                </small>
                                            </td>
                                            <td>
                                                <small>{item.createdAt || '—'}</small>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleEdit(item.id)}
                                                        title="Edit template"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteOne(item)}
                                                        title="Delete template"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant={
                                                        expandedRowId === item.id
                                                            ? 'outline-secondary'
                                                            : 'outline-primary'
                                                    }
                                                    size="sm"
                                                    onClick={() => toggleRow(item.id)}
                                                    title={expandedRowId === item.id ? 'Collapse' : 'Expand'}
                                                    aria-expanded={expandedRowId === item.id}
                                                >
                                                    <i
                                                        className={`bi bi-chevron-${
                                                            expandedRowId === item.id ? 'up' : 'down'
                                                        }`}
                                                    ></i>
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr className="p-0 border-top-0">
                                            <td colSpan={8} className="p-0 border-top-0">
                                                <Collapse in={expandedRowId === item.id}>
                                                    <div className="p-3 bg-light border-top">
                                                        <div className="row">
                                                            <DetailField label="Template ID" mono>
                                                                {item.id}
                                                            </DetailField>
                                                            <DetailField label="Title">{item.title}</DetailField>
                                                            <DetailField label="Category">{item.category}</DetailField>
                                                            <DetailField label="Status">{item.status}</DetailField>
                                                            <DetailField label="Description">
                                                                {truncateText(item.description, 200)}
                                                            </DetailField>
                                                            <DetailField label="Start Date">{item.startDate}</DetailField>
                                                            <DetailField label="End Date">{item.endDate}</DetailField>
                                                            <DetailField label="Target Response">
                                                                {item.targetResponse}
                                                            </DetailField>
                                                            <DetailField label="Max Response">
                                                                {item.maxResponse}
                                                            </DetailField>
                                                            <DetailField label="Allow Anonymous">
                                                                {item.allowAnonymous ? 'Yes' : 'No'}
                                                            </DetailField>
                                                            <DetailField label="Show Progress Bar">
                                                                {item.showProgressbar ? 'Yes' : 'No'}
                                                            </DetailField>
                                                            <DetailField label="Redirect URL">
                                                                {item.redirectUrl}
                                                            </DetailField>
                                                            <DetailField label="Created At">{item.createdAt}</DetailField>
                                                            <DetailField label="Modified At">{item.modifiedAt}</DetailField>
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '80px' }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        <span className="small">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default SurveyTable;
